<template>
    <v-container>
        <v-row class="d-flex justify-space-between">
            <v-col cols="auto">
                <h2 class="text-h5 font-weight-bold pb-5">
                    <img
                        v-if="entries.length"
                        :src="
                            entries[0].form_type === 'truck'
                                ? truckPng
                                : warePng
                        "
                        :class="[
                            entries[0].form_type === 'truck'
                                ? 'heading-icon-truck'
                                : 'heading-icon-ware',
                            'mr-2',
                        ]"
                    />
                    {{
                        entries.length
                            ? entries[0].form_type === "truck"
                                ? `${$t("entries.truckEntries")}`
                                : `${$t("entries.wareEntries")}`
                            : $t("entries.default")
                    }}
                </h2>
            </v-col>
            <v-col cols="auto">
                <v-btn
                    v-if="$route.path === '/dashboard/my-trucks'"
                    @click="$router.push('/dashboard/publish-truck')"
                    >+ {{ $t("sidebar.publishTruck") }}
                </v-btn>
                <v-btn
                    v-if="$route.path === '/dashboard/my-wares'"
                    @click="$router.push('/dashboard/publish-ware')"
                    >+ {{ $t("sidebar.publishWare") }}
                </v-btn>
            </v-col>
        </v-row>
        <EntriesFilterBar
            v-model="filters"
            :sort-direction="sortDirection"
            @toggle-sort="toggleSort"
        />

        <v-row v-if="entries.length" :data-cy="$route.name === 'MyTrucks' ? 'my-trucks-list' : 'my-wares-list'">
            <v-col
                v-for="entry in filteredAndSortedEntries"
                :key="entry.uid"
                cols="12"
                md="12"
            >
                <v-expand-transition>
                    <!-- SMALL CARD -->
                    <v-card
                        v-if="entry.uid !== expandedEntryUid"
                        :key="`small-${entry.uid}`"
                        class="d-flex flex-column chevron-card-hover"
                        :data-cy="`entry-card-${entry.uid}`"
                        @click="showCardDetails(entry.uid)"
                    >
                        <v-card-text class="font-weight-bold text-body-1">
                            <v-row>
                                <v-col cols="12">
                                    <!-- LOADING LOCATION -->
                                    ID: {{ entry.identification_id }}
                                    <img
                                        :src="loadingPng"
                                        class="entry-icon-small"
                                        alt="Loading"
                                    />
                                    {{
                                        entry.loading_location.countryName.code
                                    }}, {{ entry.loading_location.cityName }},
                                    {{ entry.loading_location.postal_code }}
                                    |
                                    {{
                                        verifyDateString(
                                            entry.loading_location.date_start,
                                            entry.loading_location.date_end
                                        )
                                    }}
                                    <span
                                        v-if="entry.loading_location.time_start"
                                        >,
                                        {{
                                            entry.loading_location.time_start?.slice(
                                                0,
                                                5
                                            )
                                        }}
                                        -
                                        {{
                                            entry.loading_location.time_end?.slice(
                                                0,
                                                5
                                            )
                                        }} </span
                                    >&nbsp;

                                    <img
                                        :src="unloadingPng"
                                        class="entry-icon-small"
                                        alt="Unloading"
                                    />
                                    {{
                                        entry.unloading_location.countryName
                                            .code
                                    }}, {{ entry.unloading_location.cityName }},
                                    {{ entry.unloading_location.postal_code }} |
                                    {{
                                        verifyDateString(
                                            entry.unloading_location.date_start,
                                            entry.unloading_location.date_end
                                        )
                                    }}
                                    <span
                                        v-if="
                                            entry.unloading_location.time_start
                                        "
                                    >,
                                        {{
                                            entry.unloading_location.time_start?.slice(
                                                0,
                                                5
                                            )
                                        }}
                                        -
                                        {{
                                            entry.unloading_location.time_end?.slice(
                                                0,
                                                5
                                            )
                                        }}
                                    </span>
                                    |
                                    {{
                                        entry.transport_options
                                            ?.truck_transport_type || "N/A"
                                    }},
                                    {{
                                        entry.transport_options
                                            ?.truck_coating || "N/A"
                                    }},

                                    <span v-if="entry.form_type === 'ware'">
                                        {{
                                            entry.transport_options
                                                ?.platform_type || "N/A"
                                        }}
                                    </span>
                                    <span
                                        v-if="
                                            entry.transport_options
                                                ?.dangerous_load
                                        "
                                    >
                                        {{ $t("formFields.dangerousLoad") }}
                                    </span>
                                    <span
                                        v-if="
                                            entry.form_type === 'ware' &&
                                            entry.transport_options
                                                .escrow_account === true
                                        "
                                        >, Escrow</span
                                    >
                                    <span v-if="entry.form_type === 'truck'">
                                        {{
                                            entry.transport_options
                                                ?.truck_type || "N/A"
                                        }}
                                    </span>
                                    <span
                                        v-if="
                                            formatSpecialFeatures(
                                                entry.transport_options
                                            )
                                        "
                                        >,
                                        {{
                                            formatSpecialFeatures(
                                                entry.transport_options
                                            )
                                        }}
                                    </span>
                                    | {{ entry.info.weight }}t/{{
                                        entry.info.volume
                                    }}m³ |
                                    <span v-if="entry.info.price"
                                        >{{ entry.info.price }}
                                        {{ entry.info.currency }}
                                        <span
                                            v-if="entry.info.vat_type === true"
                                            >{{
                                                $t("formFields.withVAT")
                                            }}</span
                                        >
                                        <span v-else
                                            >{{
                                                $t("formFields.withoutVAT")
                                            }}
                                            |</span
                                        ></span
                                    >

                                    {{ entry.info.payment_term }}

                                    <!-- DISTANCE (If available) -->
                                    <template v-if="entry.distance">
                                        <strong>{{
                                            $t("entries.distance")
                                        }}</strong>
                                        {{ entry.distance }} km
                                    </template>
                                    <span v-if="entry.notes">
                                        |
                                        <span
                                            style="
                                                font-weight: normal !important;
                                            "
                                        >
                                            {{ entry.notes }}
                                        </span>
                                    </span>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                    <!-- BIG CARD -->
                    <v-card
                        v-else
                        :key="`big-${entry.uid}`"
                        class="d-flex flex-column chevron-card-hover"
                        @click="showCardDetails(entry.uid)"
                    >
                        <!-- Toolbar -->
                        <v-toolbar color="primary" density="compact">
                            <v-toolbar-title
                                class="text-body-2 font-weight-bold"
                            >
                                {{ $t("entries.createdAt") }}:
                                {{ formatLocalTime(entry.created_at) }}
                            </v-toolbar-title>
                            <v-toolbar-items
                                v-if="entry.user.id === currentUserId"
                            >
                                <v-btn
                                    size="large"
                                    elevation="0"
                                    color="yellow"
                                    @click.stop="editEntry(entry.uid)"
                                    density="compact"
                                    class="ml-4"
                                    append-icon="mdi-file-edit-outline"
                                    >{{ $t("common.edit") }}</v-btn
                                >
                                <v-btn
                                    icon
                                    size="large"
                                    elevation="0"
                                    color="red"
                                    @click.stop="confirmDelete(entry.uid)"
                                    density="compact"
                                >
                                    <v-icon>mdi-delete</v-icon>
                                </v-btn>
                            </v-toolbar-items>
                        </v-toolbar>

                        <!-- Card Content -->
                        <v-row class="d-flex justify-space-between">
                            <v-col cols="12" sm="12" md="10" lg="11">
                                <v-card-text class="text-body-1">
                                    <v-row>
                                        <v-col cols="12">
                                            <strong style="font-size: 18px">
                                                <!-- LOADING LOCATION -->
                                                {{ $t("entries.filterId") }}:
                                                {{ entry.identification_id }}
                                                <br />

                                                <img
                                                    :src="loadingPng"
                                                    class="entry-icon-big"
                                                    alt=""
                                                />
                                                {{ $t("entries.loadingFrom") }}
                                                {{
                                                    entry.loading_location
                                                        .countryName.name
                                                }},

                                                {{
                                                    entry.loading_location
                                                        .cityName
                                                }},
                                                {{
                                                    entry.loading_location
                                                        .postal_code
                                                }}
                                                |
                                                {{
                                                    verifyDateString(
                                                        entry.loading_location
                                                            .date_start,
                                                        entry.loading_location
                                                            .date_end
                                                    )
                                                }}
                                                <span
                                                    v-if="
                                                        entry.loading_location
                                                            .time_start ||
                                                        entry.loading_location
                                                            .time_end
                                                    "
                                                    >{{
                                                        $t(
                                                            "entries.betweenHours"
                                                        )
                                                    }}
                                                    {{
                                                        entry.loading_location.time_start?.slice(
                                                            0,
                                                            5
                                                        )
                                                    }}
                                                    -
                                                    {{
                                                        entry.loading_location.time_end?.slice(
                                                            0,
                                                            5
                                                        )
                                                    }}
                                                </span>
                                                <br />
                                                <!-- UNLOADING LOCATION -->

                                                <img
                                                    :src="unloadingPng"
                                                    class="entry-icon-big"
                                                    alt=""
                                                />
                                                {{ $t("entries.destination") }}
                                                {{
                                                    entry.unloading_location
                                                        .countryName.name
                                                }},
                                                {{
                                                    entry.unloading_location
                                                        .cityName
                                                }},
                                                {{
                                                    entry.unloading_location
                                                        .postal_code
                                                }}
                                                |
                                                {{
                                                    verifyDateString(
                                                        entry.unloading_location
                                                            .date_start,
                                                        entry.unloading_location
                                                            .date_end
                                                    )
                                                }}
                                                <span
                                                    v-if="
                                                        entry.unloading_location
                                                            .time_start ||
                                                        entry.unloading_location
                                                            .time_end
                                                    "
                                                    >{{
                                                        $t(
                                                            "entries.betweenHours"
                                                        )
                                                    }}
                                                    {{
                                                        entry.unloading_location.time_start?.slice(
                                                            0,
                                                            5
                                                        )
                                                    }}
                                                    -
                                                    {{
                                                        entry.unloading_location.time_end?.slice(
                                                            0,
                                                            5
                                                        )
                                                    }}
                                                </span>
                                                <br />
                                                <!-- DISTANCE (If available) -->
                                                <template v-if="entry.distance">
                                                    {{ $t("entries.distance") }}
                                                    {{ entry.distance }} km
                                                    <br />
                                                </template>
                                                <!-- TRANSPORT REGIME -->
                                                {{
                                                    $t(
                                                        "entries.transportRegime"
                                                    )
                                                }}
                                                {{
                                                    entry.transport_options
                                                        ?.truck_transport_type ||
                                                    "N/A"
                                                }},

                                                {{
                                                    entry.transport_options
                                                        ?.truck_coating ||
                                                    "N/A"
                                                }},

                                                <span
                                                    v-if="
                                                        entry.form_type ===
                                                        'ware'
                                                    "
                                                >
                                                    {{
                                                        entry.transport_options
                                                            ?.platform_type ||
                                                        "N/A"
                                                    }}
                                                </span>
                                                <span
                                                    v-if="
                                                        entry.transport_options
                                                            ?.dangerous_load
                                                    "
                                                >
                                                    {{
                                                        $t(
                                                            "formFields.dangerousLoad"
                                                        )
                                                    }}
                                                </span>
                                                <span
                                                    v-if="
                                                        entry.form_type ===
                                                            'ware' &&
                                                        entry.transport_options
                                                            ?.escrow_account ===
                                                            true
                                                    "
                                                    >, Escrow
                                                </span>

                                                <span
                                                    v-if="
                                                        entry.form_type ===
                                                        'truck'
                                                    "
                                                >
                                                    {{
                                                        entry.transport_options
                                                            ?.truck_type ||
                                                        "N/A"
                                                    }}
                                                </span>

                                                <span
                                                    v-if="
                                                        formatSpecialFeatures(
                                                            entry.transport_options
                                                        )
                                                    "
                                                    >,
                                                    {{
                                                        formatSpecialFeatures(
                                                            entry.transport_options
                                                        )
                                                    }}
                                                </span>
                                                <br />

                                                <!-- TRUCK SPECS -->
                                                {{ $t("entries.weight") }}
                                                {{ entry.info.weight }}t.
                                                <span v-if="entry.info.volume">
                                                    {{ $t("entries.volume") }}
                                                    {{ entry.info.volume }}m³.
                                                </span>
                                                {{ $t("entries.mdp") }}
                                                {{ entry.info.mdp }}m.

                                                <!-- PRICE -->
                                                <span v-if="entry.info.price">
                                                    <br />
                                                    {{ $t("entries.price") }}
                                                    {{ entry.info.price }}
                                                    {{ entry.info.currency }}
                                                    <span
                                                        v-if="
                                                            entry.info
                                                                .vat_type ===
                                                            true
                                                        "
                                                        >{{
                                                            $t(
                                                                "formFields.withVAT"
                                                            )
                                                        }}&nbsp;</span
                                                    >
                                                    <span v-else
                                                        >{{
                                                            $t(
                                                                "formFields.withoutVAT"
                                                            )
                                                        }}&nbsp;</span
                                                    ></span
                                                >
                                                <!-- PAYMENT TERM -->
                                                {{ $t("entries.paymentTerm") }}
                                                {{ entry.info.payment_term
                                                }}<br />
                                                <span v-if="entry.notes"
                                                    >{{
                                                        $t(
                                                            "formFields.observations"
                                                        )
                                                    }}: {{ entry.notes }}</span
                                                >
                                            </strong>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-col>

                            <!-- Action Buttons -->
                            <v-col class="pt-0" cols="12" sm="12" md="2" lg="1">
                                <v-card-actions
                                    :class="
                                        $vuetify.display.mdAndUp
                                            ? 'flex-column ga-2 pt-6 pr-2 ml-n4'
                                            : 'flex-row flex-wrap ga-5 justify-center pt-0 pb-4'
                                    "
                                >
                                    <!-- Refresh Button -->
                                    <template
                                        v-if="
                                            entry.form_type &&
                                            entry.user.id === currentUserId
                                        "
                                    >
                                        <div class="d-flex align-center">
                                            <v-btn
                                                icon
                                                size="x-large"
                                                elevation="3"
                                                color="yellow"
                                                @click.stop="
                                                    refreshEntry(entry.uid)
                                                "
                                                density="compact"
                                            >
                                                <v-icon>mdi-refresh</v-icon>
                                                <v-tooltip
                                                    location="left"
                                                    activator="parent"
                                                >
                                                    {{ $t("entries.refresh") }}
                                                </v-tooltip>
                                            </v-btn>
                                        </div>
                                    </template>

                                    <MessagingButton
                                        :uid="entry.uid"
                                        :formType="entry.form_type"
                                        :userHasApp="entry.user?.hasWhatsApp"
                                        :phone="entry.user?.phone"
                                        color="green"
                                        icon="mdi-whatsapp"
                                        iconType="mdi"
                                        :tooltipText="
                                            $t('entries.infoMessageWhatsapp')
                                        "
                                        :primaryAction="openWhatsApp"
                                        :secondaryAction="openWhatsAppDownload"
                                        :secondaryActionLabel="
                                            $t('entries.downloadWhatsapp')
                                        "
                                    />

                                    <MessagingButton
                                        :uid="entry.uid"
                                        :formType="entry.form_type"
                                        :userHasApp="entry.user?.hasSignalApp"
                                        :phone="entry.user?.phone"
                                        color="blue"
                                        :icon="signalIcon"
                                        iconType="img"
                                        :tooltipText="
                                            $t('entries.infoMessageSignal')
                                        "
                                        :primaryAction="openSignal"
                                        :secondaryAction="openSignalDownload"
                                        :secondaryActionLabel="
                                            $t('entries.downloadSignal')
                                        "
                                    />
                                </v-card-actions>
                            </v-col>
                        </v-row>

                        <!-- EXPANDED DETAILS (User + Company info) -->

                        <v-row
                            v-if="expandedEntryUid === entry.uid"
                            data-cy="entry-expanded"
                            class="px-4 pb-4"
                        >
                            <v-col cols="12">
                                <v-row>
                                    <!-- User Info -->
                                    <v-col cols="12" md="6">
                                        <h4
                                            class="text-subtitle-2 font-weight-bold mb-2"
                                        >
                                            {{ $t("entries.postedBy") }}
                                        </h4>
                                        <p>
                                            <strong
                                                >{{
                                                    $t("entries.name")
                                                }}:</strong
                                            >
                                            {{ entry.user.first_name }}
                                            {{ entry.user.last_name }}<br />

                                            <strong
                                                >{{
                                                    $t("company.email")
                                                }}:</strong
                                            >
                                            <a
                                                :href="`mailto:${entry.user.email}`"
                                            >
                                                {{ entry.user.email }} </a
                                            ><br />

                                            <strong
                                                >{{
                                                    $t("company.phone")
                                                }}:</strong
                                            >
                                            {{
                                                formatPhone(
                                                    entry.user.phone_prefix,
                                                    entry.user.phone
                                                )
                                            }}<br />
                                        </p>
                                        <p
                                            class="d-flex align-center flex-wrap"
                                        >
                                            <strong
                                                >{{
                                                    $t("entries.speaks")
                                                }}:</strong
                                            >
                                            <span
                                                v-for="lang in entry.user
                                                    .spoken_languages"
                                                :key="lang"
                                                class="ml-2"
                                            >
                                                <img
                                                    :src="`/country-flags/${languageToFlag[lang]}`"
                                                    :alt="lang"
                                                    width="24"
                                                    height="24"
                                                />
                                            </span>
                                        </p>
                                    </v-col>

                                    <!-- Company Info -->
                                    <v-col
                                        cols="12"
                                        md="6"
                                        class="clickable-company-info"
                                    >
                                        <router-link
                                            :to="{
                                                name: 'CompanyDetails',
                                                params: {
                                                    id: entry.company.id,
                                                },
                                            }"
                                            class="company-router-link"
                                        >
                                            <div>
                                                <h4
                                                    class="text-subtitle-2 font-weight-bold mb-2"
                                                >
                                                    {{
                                                        $t(
                                                            "company.cardFirmaTitle"
                                                        )
                                                    }}
                                                </h4>
                                                <p>
                                                    <strong
                                                        >{{
                                                            $t(
                                                                "company.memberID"
                                                            )
                                                        }}:</strong
                                                    >
                                                    {{ entry.company.id }}<br />
                                                    <strong
                                                        >{{
                                                            $t("entries.name")
                                                        }}:</strong
                                                    >
                                                    {{ entry.company.name
                                                    }}<br />
                                                    <strong
                                                        >{{
                                                            $t(
                                                                "company.registrationNumber"
                                                            )
                                                        }}:</strong
                                                    >
                                                    {{
                                                        entry.company
                                                            .registry_combination
                                                    }}<br />
                                                    <strong
                                                        >{{
                                                            $t("company.VAT")
                                                        }}:</strong
                                                    >
                                                    {{ entry.company.VATNumber
                                                    }}<br />
                                                    <strong
                                                        v-if="entry.company.fax"
                                                        >{{
                                                            $t("company.fax")
                                                        }}:</strong
                                                    >
                                                    {{ entry.company.fax
                                                    }}<br />
                                                    <strong
                                                        v-if="
                                                            entry.company
                                                                .website
                                                        "
                                                        >{{
                                                            $t(
                                                                "register.website"
                                                            )
                                                        }}:</strong
                                                    >
                                                    <a
                                                        :href="
                                                            entry.company
                                                                .website
                                                        "
                                                        target="_blank"
                                                        @click.stop
                                                        >{{
                                                            entry.company
                                                                .website
                                                        }}</a
                                                    >
                                                </p>
                                            </div>
                                        </router-link>
                                    </v-col>
                                </v-row>
                            </v-col>
                        </v-row>
                    </v-card>
                </v-expand-transition>
            </v-col>
        </v-row>

        <v-alert v-else-if="entries.length === 0" type="warning"
            >{{ $t("entries.noEntries") }}
        </v-alert>
        <v-alert v-else type="info">{{ $t("entries.loading") }}</v-alert>
    </v-container>
</template>

<script>
import axios from "axios";
import { formatLocalTime, transformRequest, showAlertModal } from "@/utils";
import { getCityLookupBatchByCountry } from "@/api";
import signalIcon from "@/assets/signal.svg";
import emitter from "@/eventBus";
import { mapActions, mapState } from "vuex";
import MessagingButton from "@/components/MessagingButton.vue";
import {
    PAYMENT_DEADLINE,
    TRUCK_COATING,
    TRUCK_TRANSPORT_TYPE,
    TRUCK_SPECIAL_FEATURE,
    TRUCK_TYPE,
    PLATFORM_TYPE,
} from "@/enums/truckEnums.js";
import EntriesFilterBar from "@/components/display/EntriesFilterBar.vue";
import loadingPng from "@/assets/Loading.png";
import unloadingPng from "@/assets/Unloading.png";
import truckPng from "@/assets/Truck.png";
import warePng from "@/assets/Ware.png";

export default {
    components: { EntriesFilterBar, MessagingButton },
    props: {
        // When true, fetch only entries for the logged-in user
        userEntries: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            truckPng,
            warePng,
            loadingPng,
            unloadingPng,
            languageToFlag: {
                ro: "Flag-Romania.svg",
                hu: "Flag-Hungary.svg",
                en: "Flag-Great Britain.svg",
                de: "Flag-Germany.svg",
                fr: "Flag-France.svg",
                bg: "Flag-Bulgaria.svg",
                pl: "Flag-Poland.svg",
                sr: "Flag-Serbia.svg",
                hr: "Flag-Croatia.svg",
                sl: "Flag-Slovenia.svg",
                sk: "Flag-Slovakia.svg",
                it: "Flag-Italy.svg",
            },
            entries: [],
            signalIcon,
            countryLookup: [],
            cityLookupByCountry: {},
            cityLookupBatchByCountry: [],
            expandedEntryUid: null,
            filters: {
                loadingLocation: {
                    country: null,
                    city: null,
                },
                unloadingLocation: {
                    country: null,
                    city: null,
                },
                weight: null,
                mdp: null,
                identificationId: null,
            },
            sortDirection: "desc",
            highlightUid: this.$route.params.uid || null,
        };
    },
    async mounted() {
        let localStorageLanguage = localStorage.getItem("selectedLanguage");
        if (localStorageLanguage) {
            this.$store.state.selectedLanguage = localStorageLanguage;
        }

        await this.fetchFilteredCountries(); // Fetch filtered country names
        // await this.fetchCityLookup();
        await this.fetchEntries(); // Fetch entries

        emitter.on("language-changed", async (newLang) => {
            const oldLang = this.selectedLanguage();

            // ─── clear old city caches & orders ───────────
            sessionStorage.removeItem(`cityLookupByCountry_${oldLang}`);
            sessionStorage.removeItem(`cityLookupOrder_${oldLang}`);

            this.$store.state.selectedLanguage = newLang;
            await this.fetchFilteredCountries();
            await this.fetchEntries();
        });

        emitter.on("ws-update", this.handleWsUpdate);
    },
    unmounted() {
        // Clean up the event listener
        emitter.off("ws-update", this.handleWsUpdate);
        emitter.off("language-changed");
    },
    methods: {
        verifyDateString(dateStringA, dateStringB) {
            let output =
                formatLocalTime(dateStringA, "DD.MM.YYYY") ===
                formatLocalTime(dateStringB, "DD.MM.YYYY")
                    ? formatLocalTime(dateStringA, "DD.MM.YYYY")
                    : `${formatLocalTime(
                          dateStringA,
                          "DD.MM.YYYY"
                      )} - ${formatLocalTime(dateStringB, "DD.MM.YYYY")}`;
            return output;
        },
        showAlertModal,
        formatPhone(prefix, phone) {
            // Ensure we have both parts
            if (!phone) return "";
            if (!prefix) {
                // fallback: if the phone already starts with '+' then leave it
                return phone.startsWith("+") ? phone : `+${phone}`;
            }
            // Normalise: make sure prefix starts with '+'
            const cleanPrefix = prefix.startsWith("+") ? prefix : `+${prefix}`;
            // Avoid double-plus if phone already has it
            const cleanPhone = phone.replace(/^\+/, "");
            return `${cleanPrefix}${cleanPhone}`;
        },
        /**
         * Returns a comma-separated string of special features,
         * or an empty string if none exist.
         */
        formatSpecialFeatures(opts) {
            if (!opts) return "";

            const feat = opts.truck_special_features;

            // Already a translated, comma-separated string
            if (typeof feat === "string" && feat.trim().length) {
                return feat;
            }

            // Fallback to legacy scalar
            return opts.truck_special_feature || "";
        },
        toggleSort() {
            this.sortDirection = this.sortDirection === "desc" ? "asc" : "desc";
        },
        isMobile() {
            return /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
        },
        openWhatsApp(phone) {
            const cleaned = phone.replace(/\D/g, "");

            // Mobile: open WhatsApp app
            // Desktop: open WhatsApp Web
            const link = this.isMobile()
                ? `https://wa.me/${cleaned}`
                : `https://web.whatsapp.com/send?phone=${cleaned}`;

            window.open(link, "_blank");
        },
        openWhatsAppDownload() {
            window.open("https://www.whatsapp.com/download", "_blank");
        },
        openSignal(phone) {
            const cleaned = phone.replace(/[^+\d]/g, "");
            const link = `https://signal.me/#p/${cleaned}`;
            window.open(link, "_blank");
        },
        openSignalDownload() {
            window.open("https://signal.org/download/", "_blank");
        },
        /**
         * Toggle the expanded state of a card and, if opening,
         * record a view on the server and update the local view count.
         *
         * @param {string} uid - The unique identifier of the entry to expand.
         * @returns {Promise<void>}
         */
        async showCardDetails(uid) {
            const opening = this.expandedEntryUid !== uid;
            this.expandedEntryUid = opening ? uid : null;

            if (opening) {
                // increment view on server
                const isTruck = this.entries[0]?.form_type === "truck";
                const endpoint = isTruck
                    ? `${process.env.VUE_APP_API_URL}/truck-requests/${uid}/view/`
                    : `${process.env.VUE_APP_API_URL}/ware-requests/${uid}/view/`;
                try {
                    const { data } = await axios.post(endpoint);
                    // update local entry
                    const idx = this.entries.findIndex((e) => e.uid === uid);
                    if (idx !== -1) {
                        this.entries[idx].view_count = data.view_count;
                    }
                } catch (e) {
                    console.error("Failed to record view:", e);
                }
            }
        },
        async refreshEntry(uid) {
            try {
                const isTruck = this.entries[0]?.form_type === "truck";
                const endpoint = isTruck
                    ? `${process.env.VUE_APP_API_URL}/truck-requests/${uid}/refresh/`
                    : `${process.env.VUE_APP_API_URL}/ware-requests/${uid}/refresh/`;

                const response = await axios.post(endpoint);
                const updatedEntry = response.data;

                // Replace local entry (or wait for WebSocket update to handle it)
                const index = this.entries.findIndex((e) => e.uid === uid);
                if (index !== -1) {
                    this.entries.splice(index, 1, {
                        ...this.entries[index],
                        updated_at: updatedEntry.updated_at,
                    });
                }
            } catch (error) {
                console.error("Failed to refresh entry:", error);
                this.showAlertModal(
                    this.$store,
                    "Failed to refresh entry",
                    "danger",
                    12000,
                    error
                );
            }
        },
        ...mapActions(["fetchFilteredCountries"]),
        ...mapState(["selectedLanguage"]),
        getReadableValue(enumObj, value, translationKey) {
            if (value === null || value === undefined) {
                return null;
            }

            // find the enum entry (works for both arrays and plain objects)
            const match = Object.values(enumObj).find(
                (item) => item.value === value
            );
            if (!match) return "N/A";

            const valueStr = match.value.toString();

            /* ---------------------------------------------------------
             * 1) try the normal explicit translation, e.g.
             *    transport.truckType["1"]  -> "Sprinter"
             * --------------------------------------------------------- */
            const explicitKey = `transport.${translationKey}.${valueStr}`;
            if (this.$te(explicitKey)) {
                // $te = “translation exists?”
                return this.$t(explicitKey);
            }

            if (match.mu) {
                const unitKey = `transport.units.${match.mu}`;
                const unit = this.$te(unitKey) ? this.$t(unitKey) : match.mu;
                return `${match.text} ${unit}`;
            }

            /* 3) nothing else – give the plain text from enum */
            return match.text;
        },
        async handleWsUpdate(data) {
            const isTruckRoute =
                this.$route.name === "SearchTrucks" ||
                this.$route.name === "MyTrucks";
            const expectedFormType = isTruckRoute ? "truck" : "ware";

            if (data.type === "truck_delete" || data.type === "ware_delete") {
                this.entries = this.entries.filter(
                    (entry) => entry.uid !== data.entry_data.uid
                );
                return;
            }

            // Ignore updates that don't match the current form type
            if (
                !data.entry_data ||
                data.entry_data.form_type !== expectedFormType
            )
                return;

            const lang = this.selectedLanguage();
            const loadingCountryId = data.entry_data.loading_location.country;
            const unloadingCountryId =
                data.entry_data.unloading_location.country;
            const loadingCityId =
                data.entry_data.loading_location.external_city_id;
            const unloadingCityId =
                data.entry_data.unloading_location.external_city_id;

            const cityCacheKey = `cityLookupByCountry_${lang}`;
            let cityCache =
                JSON.parse(sessionStorage.getItem(cityCacheKey)) || {};

            // Check for missing cities
            const missingCityIds = [];
            const relevantCountries = new Set();

            if (
                !cityCache[loadingCountryId] ||
                !cityCache[loadingCountryId][loadingCityId]
            ) {
                missingCityIds.push(loadingCityId);
                relevantCountries.add(loadingCountryId);
            }

            if (
                !cityCache[unloadingCountryId] ||
                !cityCache[unloadingCountryId][unloadingCityId]
            ) {
                missingCityIds.push(unloadingCityId);
                relevantCountries.add(unloadingCountryId);
            }

            // Fetch missing cities if any
            if (missingCityIds.length) {
                const newCities = await getCityLookupBatchByCountry(
                    [...relevantCountries],
                    lang,
                    "",
                    missingCityIds
                );

                // ─── LRU (least recently used) CACHE (max 3 countries) ────────────────────────────
                const orderKey = `cityLookupOrder_${lang}`;
                let order = JSON.parse(sessionStorage.getItem(orderKey)) || [];

                Object.entries(newCities).forEach(([countryId, cities]) => {
                    // 1) remove old occurrence
                    order = order.filter((id) => id !== countryId);

                    // 2) merge
                    if (!cityCache[countryId]) cityCache[countryId] = {};
                    cityCache[countryId] = {
                        ...cityCache[countryId],
                        ...cities,
                    };

                    // 3) bump to most-recent
                    order.push(countryId);
                });

                // 4) evict oldest
                while (order.length > 3) {
                    const oldest = order.shift();
                    delete cityCache[oldest];
                }

                // 5) persist both
                sessionStorage.setItem(cityCacheKey, JSON.stringify(cityCache));
                sessionStorage.setItem(orderKey, JSON.stringify(order));
            }

            // Update local state
            this.cityLookupByCountry = {
                ...this.cityLookupByCountry,
                ...cityCache,
            };

            // Transform the entry using latest lookups
            const newEntry = transformRequest(
                data.entry_data,
                this.cityLookupByCountry,
                this.filteredCountries
            );

            const existingIndex = this.entries.findIndex(
                (entry) => entry.uid === newEntry.uid
            );

            if (existingIndex !== -1) {
                this.entries.splice(existingIndex, 1, newEntry); // Replace existing
            } else {
                this.entries.unshift(newEntry); // Insert at the beginning
            }
        },
        // Called when the delete button is clicked
        confirmDelete(uid) {
            if (confirm("Are you sure you want to delete this entry?")) {
                this.deleteEntry(uid);
            }
        },
        async deleteEntry(uid) {
            try {
                const isTruck = this.entries[0].form_type === "truck";
                const endpoint = isTruck
                    ? `${process.env.VUE_APP_API_URL}/truck-requests/${uid}/`
                    : `${process.env.VUE_APP_API_URL}/ware-requests/${uid}/`;

                await axios.delete(endpoint);
                // Remove the deleted entry locally (if not already removed by ws-update)
                this.entries = this.entries.filter(
                    (entry) => entry.uid !== uid
                );
            } catch (error) {
                console.error("Error deleting entry:", error);
                this.showAlertModal(
                    this.$store,
                    "Error deleting entry",
                    "danger",
                    12000,
                    error
                );
            }
        },
        editEntry(uid) {
            const entry = this.entries.find((e) => e.uid === uid);
            if (!entry || entry.user.id !== this.currentUserId) return;
            // Here we use the current route name to decide if it's truck or ware.
            const isTruck =
                this.$route.name === "SearchTrucks" ||
                this.$route.name === "MyTrucks";
            const routeName = isTruck ? "EditTruck" : "EditWare";
            this.$router.push({ name: routeName, params: { uid } });
        },

        async fetchEntries() {
            const routeName = this.$route.name;
            const isTruckRoute =
                routeName === "SearchTrucks" || routeName === "MyTrucks";
            const isUserEntries = this.userEntries;

            const baseEndpoint = isTruckRoute
                ? `${process.env.VUE_APP_API_URL}/truck-requests/`
                : `${process.env.VUE_APP_API_URL}/ware-requests/`;

            const endpoint = isUserEntries
                ? `${baseEndpoint}?my=true`
                : baseEndpoint;

            try {
                const response = await axios.get(endpoint);
                const entriesData = response.data;
                if (!entriesData.length) {
                    this.entries = [];
                    return;
                }

                // --- Extract unique country and city IDs
                const countryIds = new Set();
                const cityIds = new Set();

                entriesData.forEach((entry) => {
                    if (entry.loading_location?.country)
                        countryIds.add(entry.loading_location.country);
                    if (entry.unloading_location?.country)
                        countryIds.add(entry.unloading_location.country);
                    if (entry.loading_location?.external_city_id)
                        cityIds.add(entry.loading_location.external_city_id);
                    if (entry.unloading_location?.external_city_id)
                        cityIds.add(entry.unloading_location.external_city_id);
                });

                const lang = this.selectedLanguage();

                // --- Get filtered countries from Vuex (already populated by fetchFilteredCountries)
                const countryLookup = this.filteredCountries.reduce(
                    (acc, country) => {
                        acc[country.id] = country.name;
                        return acc;
                    },
                    {}
                );

                // --- Fetch city names using improved caching
                const cityLookupByCountry = await getCityLookupBatchByCountry(
                    [...countryIds],
                    lang,
                    "", // no search term
                    [...cityIds] // exact cities needed
                );

                // --- Transform and sort entries
                this.entries = entriesData.map((entry) =>
                    transformRequest(entry, cityLookupByCountry, countryLookup)
                );
                if (this.highlightUid) {
                    const idx = this.entries.findIndex(
                        (e) => e.uid === this.highlightUid
                    );
                    if (idx !== -1) {
                        const [hit] = this.entries.splice(idx, 1);
                        this.entries.unshift(hit); // put it first
                        setTimeout(() => {
                            this.expandedEntryUid = this.highlightUid;
                        }, 500); // auto-expand
                    }
                }

                this.cityLookupByCountry = cityLookupByCountry;
            } catch (error) {
                console.error("Error fetching entries:", error);
                this.showAlertModal(
                    this.$store,
                    "Error fetching entries",
                    "danger",
                    12000,
                    error
                );
                this.entries = [];
            }
        },

        formatLocalTime,
    },
    computed: {
        ...mapState(["filteredCountries"]), // Now this.filteredCountries comes from the store.
        currentUserId() {
            const user = JSON.parse(sessionStorage.getItem("user"));
            return user?.id || null;
        },
        filteredAndSortedEntries() {
            // 1) entries already translated & sorted
            const allEntries = this.sortedEntries;

            // 2) pull current filters
            const {
                loadingLocation,
                unloadingLocation,
                weight: maxWeight,
                mdp: maxMdp,
                identificationId, // ← already in your data
            } = this.filters;

            const loadingCountryId = loadingLocation.country;
            const loadingCityId = loadingLocation.city?.id;
            const unloadingCountryId = unloadingLocation.country;
            const unloadingCityId = unloadingLocation.city?.id;

            // 3) filter
            return allEntries.filter((entry) => {
                /* ---------- identification_id ---------- */
                if (identificationId) {
                    const needle = identificationId.trim().toUpperCase();
                    const hay = (entry.identification_id || "").toUpperCase();
                    if (!hay.includes(needle)) return false; // <-- new check
                }

                /* ---------- loading country ---------- */
                if (
                    loadingCountryId &&
                    String(entry.loading_location?.country) !==
                        String(loadingCountryId)
                )
                    return false;

                /* ---------- loading city ---------- */
                if (
                    loadingCityId &&
                    String(entry.loading_location?.external_city_id) !==
                        String(loadingCityId) &&
                    entry.loading_location.cityName !==
                        loadingLocation.city.displayName
                )
                    return false;

                /* ---------- unloading country ---------- */
                if (
                    unloadingCountryId &&
                    String(entry.unloading_location?.country) !==
                        String(unloadingCountryId)
                )
                    return false;

                /* ---------- unloading city ---------- */
                if (
                    unloadingCityId &&
                    String(entry.unloading_location?.external_city_id) !==
                        String(unloadingCityId) &&
                    entry.unloading_location.cityName !==
                        unloadingLocation.city.displayName
                )
                    return false;
                /* ---------- numeric caps ---------- */
                if (maxWeight !== null && entry.info.weight > maxWeight)
                    return false;
                if (maxMdp !== null && entry.info.mdp > maxMdp) return false;

                return true;
            });
        },
        translatedEntries() {
            // Convert filteredCountries into a lookup object
            const countryNameLookup = this.filteredCountries.reduce(
                (acc, country) => {
                    acc[country.id] = {
                        name: country.name,
                        code: country.code,
                    }; // Map country ID to translated name
                    return acc;
                },
                {}
            );

            // Convert cityLookupByCountry into a lookup object
            const cityNameLookup = {};
            if (this.cityLookupByCountry) {
                Object.keys(this.cityLookupByCountry).forEach((countryId) => {
                    Object.keys(this.cityLookupByCountry[countryId]).forEach(
                        (cityId) => {
                            cityNameLookup[cityId] =
                                this.cityLookupByCountry[countryId][cityId];
                        }
                    );
                });
            }
            return this.entries.map((entry) => {
                return {
                    ...entry,
                    modified_at: entry.modified_at,
                    loading_location: {
                        ...entry.loading_location,
                        countryName:
                            countryNameLookup[entry.loading_location.country] ||
                            "Unknown Country",
                        cityName:
                            cityNameLookup[entry.loading_location.city] ||
                            entry.loading_location.city_display_name ||
                            "Unknown City", // Use translated city name
                        countryCode:
                            countryNameLookup[entry.loading_location.country]
                                ?.code || "XX",
                    },
                    unloading_location: {
                        ...entry.unloading_location,
                        countryName:
                            countryNameLookup[
                                entry.unloading_location.country
                            ] || "Unknown Country",
                        cityName:
                            cityNameLookup[entry.unloading_location.city] ||
                            entry.unloading_location.city_display_name ||
                            "Unknown City", // Use translated city name
                        countryCode:
                            countryNameLookup[entry.loading_location.country]
                                ?.code || "XX",
                    },
                    transport_options: {
                        truck_transport_type: this.getReadableValue(
                            TRUCK_TRANSPORT_TYPE,
                            entry.transport_options?.truck_transport_type,
                            "truckTransportType"
                        ),
                        truck_type: this.getReadableValue(
                            TRUCK_TYPE,
                            entry.transport_options?.truck_type,
                            "truckType"
                        ),
                        truck_coating: this.getReadableValue(
                            TRUCK_COATING,
                            entry.transport_options?.truck_coating,
                            "truckCoating"
                        ),
                        truck_special_features: Array.isArray(
                            entry.transport_options?.truck_special_features
                        )
                            ? entry.transport_options.truck_special_features
                                  .map((val) =>
                                      this.getReadableValue(
                                          TRUCK_SPECIAL_FEATURE,
                                          val,
                                          "truckSpecialFeature"
                                      )
                                  )
                                  .join(", ")
                            : this.getReadableValue(
                                  TRUCK_SPECIAL_FEATURE,
                                  entry.transport_options
                                      ?.truck_special_feature,
                                  "truckSpecialFeature"
                              ),
                        platform_type: Array.isArray(
                            entry.transport_options?.platform_type
                        )
                            ? entry.transport_options.platform_type
                                  .map((val) =>
                                      this.getReadableValue(
                                          PLATFORM_TYPE,
                                          val,
                                          "platformType"
                                      )
                                  )
                                  .join(", ")
                            : this.getReadableValue(
                                  PLATFORM_TYPE,
                                  entry.transport_options?.platform_type,
                                  "platformType"
                              ),
                        dangerous_load: entry.transport_options?.dangerous_load,
                        escrow_account: entry.transport_options?.escrow_account,
                    },
                    info: {
                        ...entry.info,
                        payment_term: this.getReadableValue(
                            PAYMENT_DEADLINE,
                            entry.info?.payment_term,
                            "paymentTerms"
                        ),
                    },
                };
            });
        },
        sortedEntries() {
            const sorted = this.translatedEntries.slice().sort((a, b) => {
                const dateA = new Date(a.modified_at);
                const dateB = new Date(b.modified_at);
                return this.sortDirection === "desc"
                    ? dateB - dateA // newest → oldest
                    : dateA - dateB; // oldest → newest
            });
            return sorted;
        },
    },
    watch: {
        $route(to, from) {
            this.fetchEntries();
        },
    },
};
</script>

<style scoped>
.company-router-link {
    text-decoration: none;
    color: inherit;
    display: block;
    padding: 12px;
    border-radius: 8px;
    transition: background-color 0.2s ease;
}

.company-router-link:hover {
    background-color: #f2f2f2;
    cursor: pointer;
}

.chevron-jump {
    display: inline-block;
    transition: transform 0.3s ease;
}

.chevron-card-hover:hover .chevron-jump {
    animation: bounce 1.5s infinite;
}

@keyframes bounce {
    0%,
    100% {
        transform: translateY(0);
    }

    50% {
        transform: translateY(-4px);
    }
}

.entry-icon-small {
    width: 60px;
    height: 18px;
    vertical-align: top;
}

.entry-icon-big {
    width: 60px;
    height: 18px;
    vertical-align: middle;
}

.heading-icon-truck {
    width: 50px;
    height: 32px;
    vertical-align: middle;
}

.heading-icon-ware {
    width: 30px;
    height: 32px;
    vertical-align: middle;
}
</style>